// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
// ==========================================================================

import React from 'react';
import {
  DocH2,
  DocH3,
  DocP,
  DocList,
  DocTable,
  DocCode
} from './doc-bits.component';
import { LayersDiagram, InstanceDiagram, StageRail } from './diagrams.component';

/**
 * The rig template library, and what happens when a build runs.
 *
 * Written from the graphs rather than from memory: the stage order is the
 * execute_in chain in anim_rig.nxt, and `right` being an instance of `left`
 * comes off the node itself rather than an assumption about mirroring.
 */
const TemplatesDoc: React.FC = () => (
  <article className="font-body text-content-primary">
    <DocH2 id="templates">Templates and builds</DocH2>
    <DocP>
      The framework ships a library of rig templates: complete, runnable builds
      you can execute as they are, or layer your own work on top of.
    </DocP>

    <DocH3 id="what-a-template-is">What a template is</DocH3>
    <DocP>
      A template is an nxt graph, not a saved scene. It describes the steps that
      produce a rig, so running it builds the asset from its model and its data
      every time rather than opening a file somebody saved.
    </DocP>
    <DocP>
      That matters in practice: the model can be updated and the rig rebuilt
      over it, a fix made in one place is inherited by every asset built from
      it, and two people can work on different parts of the same rig without
      trading a binary back and forth.
    </DocP>
    <DocP>
      Graphs <strong>layer</strong>. A template composes the ones beneath it and
      overrides only what it needs to, which is why a whole character rig is a
      small file:
    </DocP>
    <LayersDiagram />

    <DocP>
      Your own asset becomes one more layer on top of that stack. You do not
      copy the template; you inherit it, and improvements underneath arrive on
      their own.
    </DocP>

    <DocH3 id="the-library">What is in the library</DocH3>
    <DocP>
      Templates are organised by host application, then asset type, then stage,
      then variant.
    </DocP>
    <DocTable
      head={['Host', 'Asset types', 'Variants']}
      rows={[
        [
          'Maya',
          'biped, quadruped, bird, fish, prop, vehicle',
          'default, digitigrade, unguligrade (quadruped)'
        ],
        [
          'Blender',
          'biped, quadruped',
          'default, digitigrade, unguligrade (quadruped)'
        ],
        ['Unreal', 'base layer only', 'for delivery rather than building']
      ]}
    />
    <DocP>
      The quadruped variants are the three ways a four-legged animal stands, and
      they change the leg build rather than being cosmetic presets:
    </DocP>
    <DocList
      items={[
        <>
          <strong>plantigrade</strong> (default) walks on the whole foot. Bears,
          and humans.
        </>,
        <>
          <strong>digitigrade</strong> walks on the toes, heel raised. Cats and
          dogs.
        </>,
        <>
          <strong>unguligrade</strong> walks on hoof tips. Horses and deer.
        </>
      ]}
    />
    <DocP>The stages within each asset type:</DocP>
    <DocList
      items={[
        <>
          <strong>model</strong> the mesh the rig is built onto, as an .fbx
        </>,
        <>
          <strong>rig</strong> the graph that builds the rig. This is the one
          you run.
        </>,
        <>
          <strong>cloth</strong> simulation setup layered on a finished rig
        </>,
        <>
          <strong>pub</strong> the published result, for downstream departments
        </>
      ]}
    />

    <DocH3 id="how-a-build-runs">How a build runs</DocH3>
    <DocP>
      Every rig graph inherits its order from anim_rig.nxt, so all of them run
      the same stages in the same sequence, each waiting on the one before it:
    </DocP>
    <StageRail />

    <DocTable
      head={['Stage', 'What happens']}
      rows={[
        [
          'init',
          'Everything that has to happen before the build starts. Anything you need in place first belongs here.'
        ],
        [
          'load',
          'Brings in what the rig is built from: model, joints, curves, blendshapes and materials.'
        ],
        [
          'prebuild',
          'Prepares the scene: top-level groups, the model override, the scale cluster.'
        ],
        ['center', 'Builds everything on the centre line: spine, neck, head.'],
        ['left', 'Builds the left side.'],
        [
          'right',
          'The left side again, instanced, with the side attributes flipped.'
        ],
        [
          'post_build',
          'Finishing that needs the whole rig present: control shapes, pick walking, mirrored control tags, locking hidden attributes, metadata, the framing camera.'
        ],
        [
          'apply',
          'Applies saved data back onto the rig: deformers, skin weights, lattices, wires, control defaults, deformer order, TouchPose sets.'
        ],
        [
          'delivery',
          'Prepares the rig to be handed off: cleans the hierarchy, locks nodes, applies the model override and scale, removes authoring-only sets.'
        ]
      ]}
    />
    <DocP>
      Left and right are separate stages, but the right is not built twice.{' '}
      <code>right</code> is an nxt <strong>instance</strong> of <code>left</code>
      : the same nodes with the side attributes overridden, so anything you add
      under <code>left</code> appears on the right automatically with its
      position, rotation and control colour flipped, and there is no second copy
      to keep in step.
    </DocP>

    <InstanceDiagram />

    <DocH3 id="running-a-build">Running a build</DocH3>
    <DocList
      ordered
      items={[
        'Open Maya with the framework loaded.',
        <>
          Open the rig graph for your asset type in the nxt editor, for example{' '}
          <code>templates/maya/biped/rig/default/data/biped_default_rig.nxt</code>
          .
        </>,
        'Press build.'
      ]}
    />
    <DocP>
      The graph runs top to bottom and leaves a finished rig in the scene.
      Stages can be run individually while you work: build up to post_build,
      adjust something, run apply again. That is the normal way to iterate,
      rather than rebuilding from scratch each time.
    </DocP>
    <DocP>
      Templates refer to their files by name rather than absolute path, so a
      graph does not have to know where it lives. Set{' '}
      <code>NXT_FILE_ROOTS</code> to the folders nxt should search, your own
      location first:
    </DocP>
    <DocCode>{`import os

roots = [
    "P:/show/assets/characters",              # your assets first
    "C:/path/to/templates/templates/maya",    # the shipped templates
]
os.environ["NXT_FILE_ROOTS"] = os.pathsep.join(roots)`}</DocCode>
    <DocP>
      nxt resolves in order, so a file you provide is found before the shipped
      one. That is how an asset overrides a template without editing it.
    </DocP>

    <DocH3 id="layering">Layering your own asset</DocH3>
    <DocP>
      The intended shape is one graph per asset, inheriting the template: make a
      graph, add the template as a parent layer, point it at your model, and
      override only what differs.
    </DocP>
    <DocP>
      Everything you do not override keeps coming from the template, so a fix
      made there reaches every asset built on it. Copying a template instead of
      inheriting it is the one thing worth avoiding: it works on the day, and
      then every later improvement has to be reapplied by hand to each copy.
    </DocP>
  </article>
);

export default TemplatesDoc;
